import { useEffect, useState } from "react";
import { api, describeApiError } from "@/lib/api";
import { useConfirm } from "@/components/ConfirmDialog";
import {
    Mail, Phone, Target, Inbox, CheckCircle2, Archive, Trash2,
    Loader2, RefreshCw, MessageSquare, Sparkles,
} from "lucide-react";

const STATUS_TABS = [
    { id: "all", label: "All" },
    { id: "new", label: "New" },
    { id: "contacted", label: "Contacted" },
    { id: "converted", label: "Converted" },
    { id: "archived", label: "Archived" },
];

const STATUS_STYLES = {
    new:       "border-[var(--gold)] text-[var(--gold-bright)]",
    contacted: "border-[var(--border-bright)] text-[var(--text)]",
    converted: "border-[var(--success)] text-[var(--success)]",
    archived:  "border-[var(--border)] text-[var(--text-faint)]",
};

export default function LeadsPage() {
    const [leads, setLeads] = useState([]);
    const [stats, setStats] = useState({ total: 0, new: 0, contacted: 0, converted: 0 });
    const [tab, setTab] = useState("all");
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState("");
    const [active, setActive] = useState(null);
    const confirm = useConfirm();

    const load = async (t = tab) => {
        setLoading(true);
        setErr("");
        try {
            const [{ data: l }, { data: s }] = await Promise.all([
                api.get(`/leads${t && t !== "all" ? `?status=${t}` : ""}`),
                api.get("/leads/stats"),
            ]);
            setLeads(l || []);
            setStats(s || { total: 0, new: 0, contacted: 0, converted: 0 });
        } catch (e) {
            setErr(describeApiError(e));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(tab); /* eslint-disable-next-line */ }, [tab]);

    const setStatus = async (lead, status) => {
        try {
            await api.patch(`/leads/${lead.id}`, { status });
            setLeads((arr) => arr.map((x) => x.id === lead.id ? { ...x, status } : x));
            if (active?.id === lead.id) setActive({ ...lead, status });
            load(tab);
        } catch (e) {
            setErr(describeApiError(e));
        }
    };

    const remove = async (lead) => {
        const ok = await confirm({
            title: "Delete lead?",
            message: `${lead.name} (${lead.email}) will be permanently removed.`,
            confirmLabel: "Delete",
            danger: true,
        });
        if (!ok) return;
        try {
            await api.delete(`/leads/${lead.id}`);
            setLeads((arr) => arr.filter((x) => x.id !== lead.id));
            if (active?.id === lead.id) setActive(null);
            load(tab);
        } catch (e) {
            setErr(describeApiError(e));
        }
    };

    return (
        <div className="min-h-screen p-6 lg:p-10" data-testid="leads-page">
            <div className="max-w-7xl mx-auto">
                <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-10">
                    <div>
                        <p className="label-tiny mb-2 text-[var(--gold-bright)]">/ Inbound</p>
                        <h1 className="font-serif text-5xl lg:text-6xl leading-none">Leads</h1>
                        <p className="text-[var(--text-muted)] mt-3 max-w-xl">Applications captured from the Next Level Body Lab landing page.</p>
                    </div>
                    <button onClick={() => load(tab)} className="btn-ghost text-xs inline-flex items-center gap-2 self-start lg:self-end" data-testid="leads-refresh">
                        <RefreshCw size={12} /> Refresh
                    </button>
                </div>

                <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-[var(--border)] mb-10" data-testid="leads-stats">
                    <StatTile icon={Inbox} label="Total" n={stats.total} />
                    <StatTile icon={Sparkles} label="New" n={stats.new} highlight />
                    <StatTile icon={MessageSquare} label="Contacted" n={stats.contacted} />
                    <StatTile icon={CheckCircle2} label="Converted" n={stats.converted} />
                </div>

                <div className="border-b border-[var(--border)] mb-8 flex flex-wrap gap-px bg-[var(--border)]">
                    {STATUS_TABS.map((t) => (
                        <button
                            key={t.id}
                            onClick={() => setTab(t.id)}
                            className={`bg-[var(--bg)] px-5 py-3 text-xs font-mono tracking-[0.18em] uppercase transition-colors ${
                                tab === t.id
                                    ? "text-[var(--gold-bright)] border-b-2 border-[var(--gold)]"
                                    : "text-[var(--text-muted)] hover:text-[var(--text)] border-b-2 border-transparent"
                            }`}
                            data-testid={`leads-tab-${t.id}`}
                        >
                            {t.label}
                        </button>
                    ))}
                </div>

                {err && <div className="mb-4 p-3 border border-[var(--danger)] text-sm text-[var(--danger)]">{err}</div>}

                {loading ? (
                    <div className="text-center py-20 text-[var(--text-muted)]">
                        <Loader2 className="animate-spin mx-auto mb-3" />
                        <p className="text-sm font-mono tracking-[0.2em] uppercase">Loading…</p>
                    </div>
                ) : leads.length === 0 ? (
                    <div className="text-center py-24 border border-dashed border-[var(--border-bright)]" data-testid="leads-empty">
                        <Inbox className="mx-auto mb-4 text-[var(--text-faint)]" size={32} />
                        <p className="font-serif text-2xl mb-2">No leads yet</p>
                        <p className="text-sm text-[var(--text-muted)] max-w-md mx-auto">
                            When someone submits the lead form on the landing page, they'll show up here.
                        </p>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 gap-px bg-[var(--border)]" data-testid="leads-list">
                        {leads.map((l) => (
                            <button
                                key={l.id}
                                onClick={() => setActive(l)}
                                className="bg-[var(--surface)] hover:bg-[var(--surface-hover)] transition-colors p-5 lg:p-6 text-left flex flex-col lg:flex-row lg:items-center gap-4 lg:gap-8"
                                data-testid={`lead-row-${l.id}`}
                            >
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-3 flex-wrap mb-1">
                                        <h3 className="font-serif text-2xl">{l.name}</h3>
                                        <span className={`text-[10px] font-mono tracking-[0.2em] uppercase border px-2 py-0.5 ${STATUS_STYLES[l.status] || ""}`}>
                                            {l.status}
                                        </span>
                                        {l.plan_interest && (
                                            <span className="text-[10px] font-mono tracking-[0.2em] uppercase border border-[var(--gold-deep)] text-[var(--gold-bright)] px-2 py-0.5">
                                                {l.plan_interest}
                                            </span>
                                        )}
                                    </div>
                                    <div className="text-sm text-[var(--text-muted)] flex flex-wrap gap-x-5 gap-y-1">
                                        <span className="inline-flex items-center gap-2"><Mail size={12} /> {l.email}</span>
                                        {l.phone && <span className="inline-flex items-center gap-2"><Phone size={12} /> {l.phone}</span>}
                                        {l.goal && <span className="inline-flex items-center gap-2 truncate max-w-xs"><Target size={12} /> {l.goal}</span>}
                                    </div>
                                </div>
                                <div className="text-right">
                                    <p className="text-[10px] font-mono tracking-[0.2em] uppercase text-[var(--text-faint)]">
                                        {new Date(l.created_at).toLocaleDateString()}
                                    </p>
                                    <p className="text-[10px] font-mono tracking-[0.2em] uppercase text-[var(--text-faint)]">
                                        {new Date(l.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                                    </p>
                                </div>
                            </button>
                        ))}
                    </div>
                )}
            </div>

            {active && (
                <LeadDrawer
                    lead={active}
                    onClose={() => setActive(null)}
                    onStatus={(s) => setStatus(active, s)}
                    onDelete={() => remove(active)}
                />
            )}
        </div>
    );
}

function StatTile({ icon: Icon, label, n, highlight }) {
    return (
        <div className={`bg-[var(--surface)] p-6 ${highlight ? "ring-1 ring-[var(--gold-deep)]" : ""}`}>
            <div className="flex items-center justify-between mb-3">
                <p className="label-tiny">{label}</p>
                <Icon size={14} className={highlight ? "text-[var(--gold-bright)]" : "text-[var(--text-muted)]"} />
            </div>
            <p className="font-display text-5xl gold-gradient-text leading-none">{n}</p>
        </div>
    );
}

function LeadDrawer({ lead, onClose, onStatus, onDelete }) {
    return (
        <div className="fixed inset-0 z-50 flex" onClick={onClose} data-testid="lead-drawer">
            <div className="flex-1 bg-black/70" />
            <div
                className="w-full max-w-xl bg-[var(--surface)] border-l border-[var(--gold-deep)] h-full overflow-y-auto"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="p-6 border-b border-[var(--border)] flex items-start justify-between">
                    <div>
                        <p className="label-tiny mb-2 text-[var(--gold-bright)]">/ Lead detail</p>
                        <h2 className="font-serif text-3xl">{lead.name}</h2>
                        <p className="text-sm text-[var(--text-muted)] mt-1 font-mono">{lead.email}</p>
                    </div>
                    <button onClick={onClose} className="text-[var(--text-muted)] hover:text-[var(--text)]" data-testid="lead-drawer-close">
                        ✕
                    </button>
                </div>

                <div className="p-6 space-y-6">
                    <Detail label="Status">
                        <span className={`text-xs font-mono tracking-[0.2em] uppercase border px-2 py-1 ${STATUS_STYLES[lead.status]}`}>
                            {lead.status}
                        </span>
                    </Detail>
                    {lead.phone && <Detail label="Phone"><a href={`tel:${lead.phone}`} className="text-[var(--gold-bright)] hover:underline">{lead.phone}</a></Detail>}
                    {lead.plan_interest && <Detail label="Plan interest">{lead.plan_interest}</Detail>}
                    {lead.goal && <Detail label="#1 goal">{lead.goal}</Detail>}
                    {lead.message && (
                        <Detail label="Message">
                            <p className="whitespace-pre-wrap text-sm leading-relaxed">{lead.message}</p>
                        </Detail>
                    )}
                    <Detail label="Submitted">{new Date(lead.created_at).toLocaleString()}</Detail>
                    <Detail label="Source">{lead.source || "landing"}</Detail>
                </div>

                <div className="p-6 border-t border-[var(--border)] space-y-3">
                    <a
                        href={`mailto:${lead.email}?subject=Next Level Body Lab — your application`}
                        className="btn-primary w-full inline-flex items-center justify-center gap-2"
                        data-testid="lead-email-link"
                    >
                        <Mail size={14} /> Email this lead
                    </a>
                    <div className="grid grid-cols-2 gap-3">
                        <button onClick={() => onStatus("contacted")} className="btn-ghost text-xs inline-flex items-center justify-center gap-2" data-testid="lead-status-contacted">
                            <MessageSquare size={12} /> Mark contacted
                        </button>
                        <button onClick={() => onStatus("converted")} className="btn-ghost text-xs inline-flex items-center justify-center gap-2" data-testid="lead-status-converted">
                            <CheckCircle2 size={12} /> Mark converted
                        </button>
                        <button onClick={() => onStatus("archived")} className="btn-ghost text-xs inline-flex items-center justify-center gap-2" data-testid="lead-status-archived">
                            <Archive size={12} /> Archive
                        </button>
                        <button onClick={() => onStatus("new")} className="btn-ghost text-xs inline-flex items-center justify-center gap-2" data-testid="lead-status-new">
                            <Sparkles size={12} /> Mark new
                        </button>
                    </div>
                    <button onClick={onDelete} className="w-full inline-flex items-center justify-center gap-2 py-3 border border-[var(--border)] text-[var(--danger)] hover:bg-[var(--surface-hover)] hover:border-[var(--danger)] text-xs font-mono tracking-[0.18em] uppercase" data-testid="lead-delete">
                        <Trash2 size={12} /> Delete lead
                    </button>
                </div>
            </div>
        </div>
    );
}

function Detail({ label, children }) {
    return (
        <div>
            <p className="label-tiny mb-2">{label}</p>
            <div className="text-[var(--text)]">{children}</div>
        </div>
    );
}
